import { Group } from '@mantine/core';
import { useGetWorkspaceCardDetails } from '@repo/api-codegen';
import { SettingsBlock } from '@repo/common/components/SettingsBlock';
import { Button, Text } from '@repo/foundations';
import { CardBrandLogo } from './CardBrandLogo';

/**
 * PaymentMethodSettings Component
 *
 * A reusable component for displaying and managing payment method information.
 * This component shows the current payment method and provides actions to edit or add a card.
 *
 * Usage:
 * ```tsx
 * import { PaymentMethodSettings } from './PaymentMethodSettings';
 *
 * function MyComponent() {
 *   return (
 *     <PaymentMethodSettings
 *       onEditCard={() => console.log('Edit card')}
 *       onBuyCredits={() => console.log('Buy credits')}
 *     />
 *   );
 * }
 * ```
 */
interface PaymentMethodSettingsProps {
	onEditCard?: () => void;
	onBuyCredits?: () => void;
}

export function PaymentMethodSettings({
	onEditCard,
	onBuyCredits,
}: PaymentMethodSettingsProps) {
	const { data: cardDetails } = useGetWorkspaceCardDetails({});

	const getCardDisplay = () => {
		if (!cardDetails) {
			return 'No card on file';
		}
		return `•••• ${cardDetails.last4}`;
	};

	return (
		<>
			{cardDetails?.last4 && (
				<SettingsBlock
					title="Charged to"
					description={
						<Group spacing="xs" align="center">
							{cardDetails.brand && (
								<CardBrandLogo brand={cardDetails.brand} size={36} />
							)}
							<Text size="sm">{getCardDisplay()}</Text>
						</Group>
					}
					rightComponent={
						<Group spacing="xs">
							{onEditCard && (
								<Button variant="tertiary" size="sm" onClick={onEditCard}>
									Edit
								</Button>
							)}
							{onBuyCredits && (
								<Button variant="primary" size="sm" onClick={onBuyCredits}>
									Buy credits
								</Button>
							)}
						</Group>
					}
				/>
			)}
			{!cardDetails?.last4 && (
				<SettingsBlock
					title="No card on file"
					description="Add a payment method to purchase credits"
					rightComponent={
						<Group spacing="xs">
							<Button variant="tertiary" size="sm" onClick={onEditCard}>
								Add Card
							</Button>
						</Group>
					}
				/>
			)}
		</>
	);
}
